import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import apiService from '../services/api';
import type { LocationProvider } from '../types/provider';

type Props = {
  provider: LocationProvider;
  onProviderUpdated: (provider: LocationProvider) => void;
};

function validatePassword(password: string): string | null {
  if (password.length < 8) return 'Minimum 8 caractères.';
  if (!/[A-Z]/.test(password)) return 'Au moins une majuscule.';
  if (!/\d/.test(password)) return 'Au moins un chiffre.';
  return null;
}

const ProviderAdminAccessCard: React.FC<Props> = ({ provider, onProviderUpdated }) => {
  const admin = provider.admin_user;

  const [email, setEmail] = useState(admin?.email ?? '');
  const [firstName, setFirstName] = useState(admin?.first_name ?? '');
  const [lastName, setLastName] = useState(admin?.last_name ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendAccessEmail, setSendAccessEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [accessSuccess, setAccessSuccess] = useState<string | null>(null);
  const [accessWarning, setAccessWarning] = useState<string | null>(null);

  const [notificationEmailsEnabled, setNotificationEmailsEnabled] = useState(
    provider.notification_emails_enabled !== false
  );
  const [notificationRecipientEmail, setNotificationRecipientEmail] = useState(
    provider.notification_recipient_email ?? ''
  );
  const [notifError, setNotifError] = useState<string | null>(null);
  const [notifSuccess, setNotifSuccess] = useState<string | null>(null);

  useEffect(() => {
    setEmail(provider.admin_user?.email ?? '');
    setFirstName(provider.admin_user?.first_name ?? '');
    setLastName(provider.admin_user?.last_name ?? '');
    setNotificationEmailsEnabled(provider.notification_emails_enabled !== false);
    setNotificationRecipientEmail(provider.notification_recipient_email ?? '');
    setPassword('');
    setConfirmPassword('');
    setSendAccessEmail(false);
  }, [provider]);

  const originalAdminEmail = (admin?.email ?? '').trim().toLowerCase();
  const emailChanged =
    !!admin && email.trim().toLowerCase() !== originalAdminEmail && email.trim() !== '';

  const accessMutation = useMutation({
    mutationFn: () =>
      apiService.updateProviderAdminAccess(provider.id, {
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        password: password.trim() || undefined,
        send_access_email: sendAccessEmail,
      }),
    onSuccess: (data) => {
      onProviderUpdated(data.provider);
      setPassword('');
      setConfirmPassword('');
      setSendAccessEmail(false);
      setAccessError(null);
      setAccessWarning(null);

      if (data.email_error) {
        const manualLink = data.manual_reset_url
          ? ` Vous pouvez transmettre ce lien manuellement : ${data.manual_reset_url}`
          : '';
        setAccessWarning(
          `${data.email_error} L’accès administrateur a tout de même été créé.${manualLink}`
        );
        setAccessSuccess(null);
        return;
      }

      setAccessSuccess(
        data.message ||
          (sendAccessEmail && !password.trim()
            ? 'Email d’accès envoyé à l’administrateur.'
            : 'Identifiants de connexion enregistrés.')
      );
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string } } };
      setAccessSuccess(null);
      setAccessWarning(null);
      setAccessError(ax.response?.data?.error || 'Erreur lors de l’enregistrement des accès.');
    },
  });

  const notifMutation = useMutation({
    mutationFn: () =>
      apiService.updateProvider(provider.id, {
        notification_emails_enabled: notificationEmailsEnabled,
        notification_recipient_email: notificationRecipientEmail.trim() || null,
      }),
    onSuccess: (updated) => {
      onProviderUpdated(updated);
      setNotifError(null);
      setNotifSuccess('Paramètres de notification enregistrés.');
    },
    onError: (error: unknown) => {
      const ax = error as { response?: { data?: { error?: string } } };
      setNotifSuccess(null);
      setNotifError(ax.response?.data?.error || 'Erreur lors de l’enregistrement.');
    },
  });

  const generatePasswordMutation = useMutation({
    mutationFn: () => apiService.generateProviderUserPassword(),
    onSuccess: (response) => {
      const generated = response.data?.password ?? '';
      setPassword(generated);
      setConfirmPassword(generated);
      setSendAccessEmail(false);
    },
  });

  const handleSaveAccess = () => {
    setAccessError(null);
    setAccessSuccess(null);
    setAccessWarning(null);

    if (!email.trim()) {
      setAccessError('L’email de connexion est requis.');
      return;
    }

    if (password.trim()) {
      const pwdError = validatePassword(password);
      if (pwdError) {
        setAccessError(pwdError);
        return;
      }
      if (password !== confirmPassword) {
        setAccessError('Les mots de passe ne correspondent pas.');
        return;
      }
    } else if ((!admin || emailChanged) && !sendAccessEmail) {
      setAccessError('Indiquez un mot de passe ou demandez l’envoi d’un email d’accès.');
      return;
    }

    accessMutation.mutate();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Accès dashboard &amp; notifications
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Connexion administrateur (Sherlook Pro)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {admin
            ? 'Modifiez l’email de connexion et/ou le mot de passe. Changer l’email détache l’administrateur actuel (son compte Sherlook reste inchangé) et rattache un autre compte ou en crée un nouveau.'
            : 'Aucun administrateur : créez un login et un mot de passe pour accéder au dashboard partenaire.'}
        </Typography>

        {accessError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {accessError}
          </Alert>
        )}
        {accessWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {accessWarning}
          </Alert>
        )}
        {accessSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {accessSuccess}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              required
              label="Email de connexion"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              helperText={
                emailChanged
                  ? 'L’ancien administrateur sera détaché de ce partenaire ; son email de compte ne sera pas modifié.'
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Prénom"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Nom"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                label={admin ? 'Nouveau mot de passe' : 'Mot de passe'}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="8 caractères min., 1 majuscule, 1 chiffre"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={() => generatePasswordMutation.mutate()}
                disabled={generatePasswordMutation.isPending}
                sx={{ minWidth: 120, alignSelf: 'flex-start', mt: 1 }}
              >
                Générer
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword((v) => !v)} edge="end">
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendAccessEmail}
                  onChange={(_, checked) => setSendAccessEmail(checked)}
                  disabled={!!password.trim()}
                />
              }
              label="Envoyer un email d’accès (lien pour choisir le mot de passe) au lieu de définir le mot de passe ici"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleSaveAccess}
              disabled={accessMutation.isPending}
              startIcon={accessMutation.isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {admin ? 'Enregistrer l’accès' : 'Créer l’accès administrateur'}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Notifications e-mail automatiques
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Alertes de matching Sherlook Pro et e-mails « nouveau message » vers l’administrateur / l’adresse de
          notification. Désactivez puis cliquez sur « Enregistrer les notifications » pour ne plus rien recevoir
          sur ces adresses (y compris l’email de connexion ci-dessus).
        </Typography>

        {notifError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {notifError}
          </Alert>
        )}
        {notifSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {notifSuccess}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={notificationEmailsEnabled}
              onChange={(_, checked) => setNotificationEmailsEnabled(checked)}
            />
          }
          label="Recevoir les notifications e-mail automatiques"
        />

        <TextField
          fullWidth
          sx={{ mt: 2 }}
          label="E-mail dédié aux notifications (optionnel)"
          type="email"
          value={notificationRecipientEmail}
          onChange={(e) => setNotificationRecipientEmail(e.target.value)}
          disabled={!notificationEmailsEnabled}
          helperText={
            notificationEmailsEnabled
              ? 'Si vide : e-mail de contact du partenaire, puis e-mail de l’administrateur.'
              : 'Notifications désactivées : aucun e-mail de message / matching vers l’admin ni l’adresse dédiée.'
          }
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => notifMutation.mutate()}
            disabled={notifMutation.isPending}
            startIcon={notifMutation.isPending ? <CircularProgress size={18} /> : undefined}
          >
            Enregistrer les notifications
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProviderAdminAccessCard;
